import type { DayCardPreview, MapConfig } from '../../api/types'
import { mapImage } from '../export/mapImage'
import { nadelbild } from '../map/nadelbild'
import { drawPhotoPin, PHOTO_PIN_MAP, photoPinReach } from './photoPin'
import { loadTile } from './routeFilm'

/**
 * The map of a day's route as a JPEG, drawn in the browser (#79).
 *
 * **The same way as the book's map** (`Buchkarte`, `mapImage`): a Leaflet
 * map that nobody sees, off the screen, the tiles from the map server as
 * on every map here -- the first exception in `CLAUDE.md`, and no new
 * one. When the tiles are there (or the map server stays silent for
 * twelve seconds) the map is copied to a canvas and comes back as a data
 * URL; the server stores it as the post's photo.
 *
 * **The published points of the day stand on it as pins** (#80), the same
 * `nadelbild()` as on the map, in the trip's colour for a published point:
 * the pin reads its colour from `--nadel` on the box, and `mapImage()`
 * copies the computed colours into the picture, as it does for the book.
 *
 * **A point with a photo stands with it** (1.127.0), as on the map and in
 * the film. From use: "wenn nur ein Bild ausgegeben wird, sollten die POIs
 * ihre Bilder auch anzeigen". The photo pin on the map is HTML, and
 * `mapImage()` copies only a pin's SVG -- so these pins are no Leaflet
 * markers but painted on top (`drawPhotoPin()`, the film's shape at the
 * map's size). A photo that does not load leaves the drop: it costs the
 * picture, never the pin.
 *
 * `null` when there is nothing to draw or the canvas cannot be read (a
 * tile without CORS): then the card goes out without a picture.
 */
export async function zeichneTagesroute(
  punkte: [number, number][],
  quelle: MapConfig,
  strich: { farbe: string; breite: number; nadel: string },
  nadeln: DayCardPreview['highlights'] = [],
): Promise<string | null> {
  if (punkte.length < 2 || typeof document === 'undefined') return null
  // Leaflet comes with the first picture, not with the chat (1.153.0,
  // review D3): this module is reached from the plus menu, and a static
  // import here carried the whole library (150 KB) into the main bundle
  // for every reader who never draws a day card. The map page and the book
  // fetch the same chunk, so it is loaded once.
  const [{ default: L }, { stillMap }] = await Promise.all([import('leaflet'), import('../../lib/stillMap')])
  const kasten = document.createElement('div')
  kasten.setAttribute('aria-hidden', 'true')
  kasten.style.cssText = 'position:fixed;left:-10000px;top:0;width:640px;height:400px;'
  kasten.style.setProperty('--nadel', strich.nadel)
  document.body.appendChild(kasten)
  const still = stillMap(kasten, quelle, { animate: false })
  const karte = still.map
  try {
    const fertig = still.ready
    karte.setMaxZoom(Math.min(quelle.max_zoom, 16))
    const linie = L.polyline(punkte, { color: strich.farbe, weight: strich.breite }).addTo(karte)
    // Start and end as small discs on the line, in the line's colour.
    L.circleMarker(punkte[0]!, { radius: 6, color: '#ffffff', weight: 2, fillColor: strich.farbe, fillOpacity: 1 }).addTo(karte)
    L.circleMarker(punkte[punkte.length - 1]!, { radius: 6, color: '#ffffff', weight: 2, fillColor: strich.farbe, fillOpacity: 1 }).addTo(karte)
    // The photos first: a pin is painted with one only when it is there.
    const fotos = new Map<string, HTMLImageElement>()
    for (const url of new Set(nadeln.map((n) => n.image_url).filter((u): u is string => typeof u === 'string' && u !== ''))) {
      const bild = await loadTile(url)
      if (bild !== null) fotos.set(url, bild)
    }
    const mitFoto = nadeln
      .filter((n) => typeof n.image_url === 'string' && fotos.has(n.image_url))
      // Newest last, so on top, as in the film.
      .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? ''))
    for (const nadel of nadeln) {
      if (mitFoto.includes(nadel)) continue
      L.marker([nadel.lat, nadel.lon], {
        icon: L.divIcon({ className: '', html: nadelbild('nadel'), iconSize: [22, 30], iconAnchor: [11, 30] }),
        keyboard: false,
        interactive: false,
      }).addTo(karte)
    }
    // The frame holds the line; a pin off the line is a point of the day
    // and belongs in the picture as well.
    const rahmen = linie.getBounds()
    for (const nadel of nadeln) rahmen.extend([nadel.lat, nadel.lon])
    // A photo pin reaches about fifty points above its point; a point at the
    // top edge would lose its ring to the frame.
    const oben = 28 + (mitFoto.length > 0 ? photoPinReach(PHOTO_PIN_MAP) : 0)
    karte.fitBounds(rahmen, { paddingTopLeft: [28, oben], paddingBottomRight: [28, 28] })
    await fertig
    // One more frame for the tiles to be painted where Leaflet put them.
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
    return await mapImage(kasten, 2, (ctx) => {
      const box = kasten.getBoundingClientRect()
      const container = karte.getContainer().getBoundingClientRect()
      for (const nadel of mitFoto) {
        const p = karte.latLngToContainerPoint([nadel.lat, nadel.lon])
        drawPhotoPin(ctx, container.left - box.left + p.x, container.top - box.top + p.y, fotos.get(nadel.image_url!)!, strich.nadel, PHOTO_PIN_MAP)
      }
    })
  } catch {
    return null
  } finally {
    still.dispose()
    kasten.remove()
  }
}
